// PhonicsLt.js
import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StarIcon from '@mui/icons-material/Star';
import PhonicsSong from './PhonicsSong';
import PhonicsReviewPic from './PhonicsReviewPic';
import PhonicsStory from './PhonicsStory';
import PhonicsThanks from './PhonicsThanks';
import RewardEffect from './RewardEffect';
import { usePhonicsNavigation } from './usePhonicsNavigation';

/** Letter Teams 共用指令音频目录（所有 LT 课件共用 lt01 下的指令音频） */
const LT_INSTRUCTION_AUDIO = '/media/phonics/lt01';

function buildSteps(data) {
  const mb = data.mediaBase;
  return [
    {
      key: 'part1',
      label: 'P1',
      renderFn: () => (
        <PhonicsSong
          data={data.part1Video}
          title="Watch the video"
          titleCn="看视频"
          instructionAudio={`${LT_INSTRUCTION_AUDIO}/audio_instruction_watch.mp3`}
          inlineCn
        />
      ),
    },
    {
      key: 'part3video',
      label: 'P2',
      renderFn: () => (
        <PhonicsSong
          data={data.part3Video}
          title="Repeat the words"
          titleCn="跟读单词"
          instructionAudio={`${LT_INSTRUCTION_AUDIO}/audio_instruction_repeat_words.mp3`}
          inlineCn
        />
      ),
    },
    {
      key: 'review',
      label: 'P3',
      renderFn: () => (
        <PhonicsReviewPic
          src={data.reviewPic.src}
          title="Say the words"
          titleCn="读单词"
          instructionAudio={`${LT_INSTRUCTION_AUDIO}/audio_instruction_say_words.mp3`}
          inlineCn
        />
      ),
    },
    // storySections 统一遍历（layout='story-video-left-lines-right'）
    ...(data.storySections || []).map((section, i) => ({
      key: `story${i}`,
      label: section.label || `Story ${i + 1}`,
      renderFn: () => (
        <PhonicsStory
          data={section}
          mediaBase={mb}
          layout={section.layout || 'story-video-left-lines-right'}
        />
      ),
    })),
    {
      key: 'thanks',
      label: '完成',
      renderFn: () => <PhonicsThanks mediaBase={LT_INSTRUCTION_AUDIO} />,
    },
  ];
}

function PhonicsLt({ letterData: data }) {
  const STEPS = buildSteps(data);
  const { currentStep, setCurrentStep, isFullscreen, goNext, goPrev, toggleFullscreen, isFirst, isLast } = usePhonicsNavigation(STEPS);
  const [rewardTrigger, setRewardTrigger] = useState(0);

  return (
    <div className="phonics-root">
      {/* 奖励效果 */}
      <RewardEffect
        key={rewardTrigger}
        trigger={rewardTrigger > 0}
        onDone={() => setRewardTrigger(0)}
      />

      <div className="phonics-content">
        {STEPS[currentStep].renderFn()}
      </div>
      <div className="phonics-footer">
        <Tooltip title="上一步">
          <IconButton
            size="small"
            onClick={goPrev}
            disabled={isFirst}
            className="phonics-nav-btn"
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <div className="phonics-progress">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div
                className={'phonics-progress-dot' + (idx === currentStep ? ' active' : '')}
                title={step.label}
                onClick={() => setCurrentStep(idx)}
                style={{ cursor: 'pointer' }}
              />
              {idx < STEPS.length - 1 && (
                <div className="phonics-progress-line" />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Tooltip title="太棒了！">
            <IconButton size="small" onClick={() => setRewardTrigger(t => t + 1)} className="phonics-nav-btn" sx={{ color: '#FFD700' }}>
              <StarIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
            <IconButton size="small" onClick={toggleFullscreen} className="phonics-nav-btn">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="下一步">
            <IconButton
              size="small"
              onClick={goNext}
              disabled={isLast}
              className="phonics-nav-btn"
            >
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

export default PhonicsLt;
